<script setup>
import ECommerceModulesActionsCell from "@frontJs/components/tables/cells/projectManagement/dev/ECommerceModulesActionsCell.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { userSessionKey, type } = defineProps({
  userSessionKey: {
    type: String,
    required: false,
    default: null,
  },
  type: {
    type: String,
    required: false,
    default: null,
  },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  filters,
  availableFilters,
  setGlobalFilter,
  setFilters,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.name,
          href: route("front.projectManagement.dev.eCommerceModules", {
            tenant: props.row.original.id,
          }),
          inertia: true,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "cms",
      header: () => t("label.cms"),
      cell: (props) =>
        h(TextCellV2, {
          text:
            props.row.original.cms === "magento1"
              ? "Magento 1"
              : props.row.original.cms === "magento2"
                ? "Magento 2"
                : props.row.original.cms === "prestashop"
                  ? "Prestashop"
                  : props.row.original.cms === "wordpress"
                    ? "Wordpress"
                    : props.row.original.cms === "shopify"
                      ? "Shopify"
                      : "Custom",
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.moduleNames"),
      cell: (props) =>
        props.row.original.eCommerceModules.length > 0
          ? h(
              "ul",
              {
                class: "p-4 pl-8 list-disc",
              },
              props.row.original.eCommerceModules.map((module) =>
                h("li", { innerText: module.name }),
              ),
            )
          : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "version",
      header: () => t("label.moduleVersions"),
      cell: (props) =>
        props.row.original.eCommerceModules.length > 0
          ? h(
              "ul",
              {
                class: "p-4 pl-8 list-disc",
              },
              props.row.original.eCommerceModules.map((module) =>
                h("li", { innerText: module.version ? module.version : "N/A" }),
              ),
            )
          : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(ECommerceModulesActionsCell, {
          row: innerProps.row,
        }),
    }),
  ],
  url: route("front.projectManagement.dev.postECommerceModules"),
  data: {
    type,
  },
  userSessionKey: userSessionKey,
  columnPinning: {
    right: ["actions"],
  },
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :filters="filters"
    :available-filters="availableFilters"
    :set-filters="setFilters"
    :set-global-filter="setGlobalFilter"
  />
</template>
